import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert } from '../ui';
import { AlertTriangle, FileText, TrendingUp, AlertCircle } from 'lucide-react';
import { api } from '../../services/api';

interface DependentPrompt {
  id: string;
  name: string;
  description: string;
  use_count: number;
  created_at: string;
  is_required?: boolean;
  execution_order?: number;
  source?: 'junction_table' | 'json_field';
}

interface ToolDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  toolId: string;
  toolName: string;
}

export const ToolDeletionModal: React.FC<ToolDeletionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  toolId,
  toolName
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dependentPrompts, setDependentPrompts] = useState<DependentPrompt[]>([]);
  const [totalUsage, setTotalUsage] = useState(0);
  const [recommendation, setRecommendation] = useState<'safe' | 'low_impact' | 'medium_impact' | 'high_impact'>('safe');
  const [confirmationText, setConfirmationText] = useState('');

  useEffect(() => {
    if (isOpen && toolId) {
      fetchDependencies();
    }
  }, [isOpen, toolId]);

  const fetchDependencies = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.tools.getDependentPrompts(toolId);
      setDependentPrompts(response.dependent_prompts);
      setTotalUsage(response.impact_analysis.total_usage);
      setRecommendation(response.impact_analysis.recommendation as any);
    } catch (err: any) {
      setError(err.message || 'Failed to load dependent prompts');
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationColor = () => {
    switch (recommendation) {
      case 'safe': return 'text-green-600';
      case 'low_impact': return 'text-yellow-600';
      case 'medium_impact': return 'text-orange-600';
      case 'high_impact': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getRecommendationIcon = () => {
    switch (recommendation) {
      case 'safe': return <AlertCircle size={24} className="text-green-600" />;
      case 'low_impact': return <AlertTriangle size={24} className="text-yellow-600" />;
      case 'medium_impact': return <AlertTriangle size={24} className="text-orange-600" />;
      case 'high_impact': return <AlertTriangle size={24} className="text-red-600" />;
      default: return <AlertCircle size={24} className="text-gray-600" />;
    }
  };

  const getRecommendationText = () => {
    switch (recommendation) {
      case 'safe':
        return 'Safe to delete - No prompts depend on this tool';
      case 'low_impact':
        return 'Low Impact - Few prompts depend on this tool';
      case 'medium_impact':
        return 'Medium Impact - Several prompts use this tool';
      case 'high_impact':
        return 'High Impact - Many prompts heavily depend on this tool';
      default:
        return 'Unknown impact';
    }
  };

  const handleConfirm = () => {
    // For high impact tools, require confirmation text
    if (recommendation === 'high_impact' && confirmationText !== toolName) {
      setError(`Please type "${toolName}" to confirm deletion`);
      return;
    }
    onConfirm();
  };

  const requiresConfirmation = recommendation === 'high_impact';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Tool - Impact Analysis"
      size="lg"
    >
      <div className="space-y-6">
        {/* Tool Info */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              {getRecommendationIcon()}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">
                Deleting: <span className="text-blue-600">{toolName}</span>
              </h3>
              <p className={`text-sm font-medium mt-1 ${getRecommendationColor()}`}>
                {getRecommendationText()}
              </p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
            <span className="ml-3 text-gray-600">Analyzing dependencies...</span>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Dependency Analysis */}
        {!loading && !error && (
          <>
            {/* Stats Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={20} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">Dependent Prompts</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {dependentPrompts.length}
                </div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp size={20} className="text-purple-600" />
                  <span className="text-sm font-medium text-purple-900">Total Usage</span>
                </div>
                <div className="text-2xl font-bold text-purple-600">
                  {totalUsage}
                </div>
              </div>
            </div>

            {/* Dependent Prompts List */}
            {dependentPrompts.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">
                  Prompts that will be affected:
                </h4>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {dependentPrompts.map((prompt) => (
                    <div
                      key={prompt.id}
                      className="bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <FileText size={16} className="text-gray-600 flex-shrink-0" />
                            <span className="font-medium text-gray-900">{prompt.name}</span>
                            {prompt.is_required && (
                              <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                                Required
                              </span>
                            )}
                          </div>
                          {prompt.description && (
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {prompt.description}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <span>Used: {prompt.use_count} times</span>
                            {prompt.source && (
                              <span className="px-1.5 py-0.5 bg-gray-100 rounded">
                                {prompt.source === 'junction_table' ? 'Structured' : 'Legacy'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warning Message */}
            {dependentPrompts.length > 0 && (
              <Alert variant="warning">
                <div className="space-y-2">
                  <p className="font-semibold">Warning: This action cannot be undone</p>
                  <p className="text-sm">
                    Deleting this tool will affect {dependentPrompts.length} prompt{dependentPrompts.length !== 1 ? 's' : ''}.
                    These prompts may fail or produce errors when executed without this tool.
                  </p>
                  <p className="text-sm font-medium">
                    Consider updating or removing these prompts before deleting the tool.
                  </p>
                </div>
              </Alert>
            )}

            {/* Confirmation Input for High Impact */}
            {requiresConfirmation && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Type <span className="font-bold text-red-600">{toolName}</span> to confirm deletion:
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder={toolName}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={loading || (requiresConfirmation && confirmationText !== toolName)}
          >
            {recommendation === 'safe' ? 'Delete Tool' : `Delete Anyway (${dependentPrompts.length} affected)`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
